import { FC, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPinOff } from 'lucide-react';

import { BerthPlanVisit } from '@/components/blocks/berth-plan/types';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { formatDateForDisplay } from '@/lib/utils';

const UnplacedVisitLabel: FC<{ visit: BerthPlanVisit }> = ({ visit }) => {
  const { t } = useTranslation();
  return (
    <>
      <span className='text-sm font-medium'>{visit.ship_id}</span>
      <span className='text-xs text-muted-foreground'>
        {t('berthPlan.unplaced.item', {
          service: visit.shipping_line_id,
          arrival: formatDateForDisplay(visit.arrival_time),
        })}
      </span>
    </>
  );
};

interface UnplacedVisitsPopoverProps {
  visits: Array<BerthPlanVisit>;
  selectedVisitId: string | null;
  onSelect: (visitId: string) => void;
  onDeselect: () => void;
  /** Absent on a read-only plan: the list is informational. */
  renderEditor?: (visitId: string) => ReactNode;
}

/**
 * Visits the Berth Plan can't draw, behind a badge. Picking one swaps the
 * list for its editor; giving it bollards places it on the plan.
 */
const UnplacedVisitsPopover: FC<UnplacedVisitsPopoverProps> = ({
  visits,
  selectedVisitId,
  onSelect,
  onDeselect,
  renderEditor,
}) => {
  const { t } = useTranslation();
  const [isListOpen, setIsListOpen] = useState(false);
  const selected = visits.find((visit) => visit.id === selectedVisitId);

  if (visits.length === 0) return null;

  return (
    <Popover
      open={isListOpen || selected !== undefined}
      onOpenChange={(open) => {
        setIsListOpen(open);
        if (!open && selected) onDeselect();
      }}>
      <PopoverTrigger asChild>
        <Button
          variant='outline'
          size='sm'
          className='h-8 text-xs'>
          <MapPinOff className='size-3.5' />
          {t('berthPlan.unplaced.button', {
            count: visits.length,
          })}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        side='bottom'
        align='start'
        collisionPadding={16}
        className='z-[110] w-80'>
        {selected && renderEditor ? (
          renderEditor(selected.id)
        ) : (
          <div className='flex flex-col gap-2'>
            <div>
              <p className='text-sm font-medium'>
                {t('berthPlan.unplaced.title')}
              </p>
              <p className='text-xs text-muted-foreground'>
                {t('berthPlan.unplaced.hint')}
              </p>
            </div>
            <ul className='-mx-2 max-h-72 overflow-y-auto'>
              {visits.map((visit) => (
                <li key={visit.id}>
                  {renderEditor ? (
                    <button
                      type='button'
                      onClick={() => {
                        onSelect(visit.id);
                      }}
                      className='flex w-full flex-col rounded-md px-2 py-1.5 text-left hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'>
                      <UnplacedVisitLabel visit={visit} />
                    </button>
                  ) : (
                    <div className='flex flex-col px-2 py-1.5'>
                      <UnplacedVisitLabel visit={visit} />
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default UnplacedVisitsPopover;
